// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/Messages.css";
import {
  fetchConversationsWithSignal,
  fetchConversationById,
  sendMessage,
  deleteMessage,
  deleteConversation,
  markConversationRead as markConversationReadRequest,
} from "../api/conversations";
import { getUser } from "../auth/auth";
import UserActionsMenu from "../components/UserActionsMenu";
import TopNav from "../components/TopNav";
import ReportUserModal from "../components/ReportUserModal";
import { useToast } from "../components/ToastProvider";
import { containsBannedTerms } from "../utils/contentModeration";
import { RealtimeEvents } from "../realtime/events";
import { useRealtimeSocket } from "../realtime/useRealtime";
import imageCompression from "browser-image-compression";

export default function Messages() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = getUser();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [deletingThreadId, setDeletingThreadId] = useState("");
  const [scamWarning, setScamWarning] = useState(false);
  const [profanityWarning, setProfanityWarning] = useState(false);
  const [bannedError, setBannedError] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const attachmentInputRef = useRef(null);
  const [reportModal, setReportModal] = useState({ open: false, messageId: null, senderId: null, senderName: null });
  const realtime = useRealtimeSocket();
  const seenMessageEventKeysRef = useRef(new Set());
  const { confirm: confirmToast, showToast } = useToast();
  const currentUserId = String(user?.id || "");

  useEffect(() => {
    seenMessageEventKeysRef.current.clear();
  }, [currentUserId]);

  useEffect(() => {
    let activeFlag = true;
    const controller = new AbortController();
    setLoading(true);
    setError("");

    fetchConversationsWithSignal(controller.signal)
      .then((data) => {
        if (!activeFlag) return;
        setConversations(data || []);
      })
      .catch((err) => {
        if (!activeFlag) return;
        if (String(err?.message || "").toLowerCase().includes("canceled")) return;
        setError("Failed to load conversations");
      })
      .finally(() => {
        if (activeFlag) setLoading(false);
      });

    return () => {
      activeFlag = false;
      controller.abort();
    };
  }, []);

  const selectedId = useMemo(() => {
    if (id) return id;
    if (active?.id) return active.id;
    return conversations[0]?.id || "";
  }, [id, active?.id, conversations]);

  const setConversationUnreadCountLocal = useCallback((conversationId, unreadCount) => {
    const normalizedUnreadCount = Math.max(0, Number(unreadCount || 0));
    setConversations((prev) =>
      prev.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, unreadCount: normalizedUnreadCount }
          : conversation,
      ),
    );
    setActive((prev) =>
      prev && prev.id === conversationId
        ? { ...prev, unreadCount: normalizedUnreadCount }
        : prev,
    );
  }, []);

  const markConversationRead = useCallback(
    async (conversationId) => {
      if (!conversationId) return;
      setConversationUnreadCountLocal(conversationId, 0);
      try {
        await markConversationReadRequest(conversationId);
      } catch (error) {
        // keep optimistic clear to avoid stale badge flicker
      }
    },
    [setConversationUnreadCountLocal],
  );

  useEffect(() => {
    if (!selectedId) {
      setActive(null);
      return;
    }

    let activeFlag = true;
    const controller = new AbortController();
    setThreadLoading(true);

    fetchConversationById(selectedId, controller.signal)
      .then((data) => {
        if (!activeFlag) return;
        setActive(data);
      })
      .catch((err) => {
        if (!activeFlag) return;
        if (String(err?.message || "").toLowerCase().includes("canceled")) return;
        setActive(null);
      })
      .finally(() => {
        if (activeFlag) setThreadLoading(false);
      });

    return () => {
      activeFlag = false;
      controller.abort();
    };
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    const selectedConversation = conversations.find((conversation) => conversation.id === selectedId);
    if (!selectedConversation) return;
    if (Number(selectedConversation.unreadCount || 0) <= 0) return;
    void markConversationRead(selectedId);
  }, [conversations, markConversationRead, selectedId]);

  useEffect(() => {
    if (!selectedId || !user?.id) return;
    realtime.emit(RealtimeEvents.ChatJoinConversation, { conversationId: selectedId });
    return () => {
      realtime.emit(RealtimeEvents.ChatLeaveConversation, { conversationId: selectedId });
    };
  }, [realtime, selectedId, user?.id]);

  useEffect(() => {
    if (!currentUserId) return;

    const onMessageCreated = (payload) => {
      const conversationId = String(payload?.conversationId || "").trim();
      const incomingMessage = payload?.message;
      const incomingMessageId = String(incomingMessage?.id || "").trim();
      const incomingSenderId = String(incomingMessage?.senderId || "").trim();
      if (!conversationId || !incomingMessage || !incomingMessageId) return;

      const dedupeKey = `${conversationId}:${incomingMessageId}`;
      if (seenMessageEventKeysRef.current.has(dedupeKey)) return;
      seenMessageEventKeysRef.current.add(dedupeKey);
      if (seenMessageEventKeysRef.current.size > 1000) {
        const firstKey = seenMessageEventKeysRef.current.values().next().value;
        if (firstKey) {
          seenMessageEventKeysRef.current.delete(firstKey);
        }
      }

      const isIncomingFromOtherUser =
        Boolean(incomingSenderId) && incomingSenderId !== currentUserId;
      const isSelectedThread = String(selectedId || "") === conversationId;

      setConversations((prev) => {
        const target = prev.find((c) => c.id === conversationId);
        const updatedAt = payload?.conversationPreview?.updatedAt || incomingMessage.createdAt || new Date().toISOString();
        const lastMessage = payload?.conversationPreview?.lastMessage || incomingMessage.body || "";
        const next = target
          ? prev.map((c) => {
              if (c.id !== conversationId) return c;
              const currentUnreadCount = Number(c.unreadCount || 0);
              const nextUnreadCount =
                isIncomingFromOtherUser && !isSelectedThread
                  ? currentUnreadCount + 1
                  : isSelectedThread
                  ? 0
                  : currentUnreadCount;
              return { ...c, lastMessage, updatedAt, unreadCount: nextUnreadCount };
            })
          : payload?.conversationPreview
          ? [
              {
                id: conversationId,
                listingTitle: payload.conversationPreview.listingTitle || "Conversation",
                sellerId: payload.conversationPreview.sellerId,
                buyerId: payload.conversationPreview.buyerId,
                sellerName: payload.conversationPreview.sellerName || "Cardex Trader",
                buyerName: payload.conversationPreview.buyerName || "Cardex Trader",
                lastMessage,
                updatedAt,
                unreadCount: isIncomingFromOtherUser && !isSelectedThread ? 1 : 0,
                messages: [],
              },
              ...prev,
            ]
          : prev;
        return [...next].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      });

      setActive((prev) => {
        if (!prev || prev.id !== conversationId) return prev;
        const existing = Array.isArray(prev.messages) ? prev.messages : [];
        const nextMessages = existing.map((m) => {
          const idMatch = String(m.id) === String(incomingMessage.id);
          const clientMatch =
            incomingMessage.clientMessageId &&
            m.clientMessageId &&
            String(m.clientMessageId) === String(incomingMessage.clientMessageId);
          if (idMatch || clientMatch) {
            return { ...incomingMessage, status: "sent", error: undefined };
          }
          return m;
        });
        const exists = nextMessages.some((m) => String(m.id) === String(incomingMessage.id));
        if (!exists) {
          nextMessages.push({ ...incomingMessage, status: "sent", error: undefined });
        }
        return {
          ...prev,
          messages: nextMessages,
          lastMessage: incomingMessage.body || prev.lastMessage,
          updatedAt: incomingMessage.createdAt || prev.updatedAt,
          unreadCount: isSelectedThread ? 0 : Number(prev.unreadCount || 0),
        };
      });

      if (isIncomingFromOtherUser && isSelectedThread) {
        void markConversationRead(conversationId);
      }
    };

    const onConversationUpdated = (payload) => {
      const conversationId = payload?.conversationId;
      if (!conversationId) return;
      const updatedAt = payload?.updatedAt || new Date().toISOString();
      const lastMessage = payload?.lastMessage || "";
      setConversations((prev) => {
        const next = prev.map((c) => (c.id === conversationId ? { ...c, lastMessage, updatedAt } : c));
        return [...next].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      });
      setActive((prev) =>
        prev && prev.id === conversationId ? { ...prev, lastMessage, updatedAt } : prev
      );
    };

    realtime.on(RealtimeEvents.ChatMessageCreated, onMessageCreated);
    realtime.on(RealtimeEvents.ChatConversationUpdated, onConversationUpdated);

    return () => {
      realtime.off(RealtimeEvents.ChatMessageCreated, onMessageCreated);
      realtime.off(RealtimeEvents.ChatConversationUpdated, onConversationUpdated);
    };
  }, [currentUserId, markConversationRead, realtime, selectedId]);

  const handleSelect = (conversationId) => {
    navigate(`/messages/${conversationId}`);
  };

  const buildClientMessageId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const upsertConversationPreview = (conversationId, lastMessage, updatedAt) => {
    setConversations((prev) => {
      const exists = prev.some((c) => c.id === conversationId);
      const next = exists
        ? prev.map((c) => (c.id === conversationId ? { ...c, lastMessage, updatedAt } : c))
        : [
            {
              id: conversationId,
              listingTitle: active?.listingTitle || "Conversation",
              listingGame: active?.listingGame || "",
              listingPrice: active?.listingPrice || 0,
              sellerId: active?.sellerId,
              buyerId: active?.buyerId,
              sellerName: active?.sellerName || "Cardex Trader",
              buyerName: active?.buyerName || "Cardex Trader",
              lastMessage,
              updatedAt,
              unreadCount: 0,
              messages: [],
            },
            ...prev,
          ];
      return [...next].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    });
  };

  const applyConfirmedMessage = (conversationId, messageDto, updatedAt, lastMessage) => {
    setActive((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      const existing = Array.isArray(prev.messages) ? prev.messages : [];
      const replaced = existing.map((m) => {
        if (messageDto?.clientMessageId && m.clientMessageId === messageDto.clientMessageId) {
          return { ...messageDto, status: "sent", error: undefined };
        }
        return m;
      });
      const hasById = replaced.some((m) => String(m.id) === String(messageDto.id));
      const nextMessages = hasById ? replaced : [...replaced, { ...messageDto, status: "sent", error: undefined }];
      return {
        ...prev,
        messages: nextMessages,
        lastMessage: lastMessage || messageDto.body || prev.lastMessage,
        updatedAt: updatedAt || messageDto.createdAt || prev.updatedAt,
      };
    });
  };

  const markFailedByClientMessageId = (conversationId, clientMessageId, errorText) => {
    setActive((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      return {
        ...prev,
        messages: (prev.messages || []).map((m) =>
          m.clientMessageId === clientMessageId
            ? { ...m, status: "failed", error: errorText || "Failed to send" }
            : m
        ),
      };
    });
  };

  const sendWithOptimistic = async (text, retryClientMessageId, attachmentArg) => {
    if (!active) return;
    const body = String(text || "").trim();
    if (!body && !attachmentArg) return;

    const clientMessageId = retryClientMessageId || buildClientMessageId();
    const optimisticMessage = {
      id: `tmp:${clientMessageId}`,
      clientMessageId,
      senderId: user?.id,
      body,
      attachmentUrl: attachmentArg || null,
      createdAt: new Date().toISOString(),
      status: "pending",
      error: undefined,
    };

    setActive((prev) => {
      if (!prev || prev.id !== active.id) return prev;
      const existing = Array.isArray(prev.messages) ? prev.messages : [];
      const deduped = retryClientMessageId
        ? existing.filter((m) => m.clientMessageId !== retryClientMessageId)
        : existing;
      return {
        ...prev,
        messages: [...deduped, optimisticMessage],
        lastMessage: body,
        updatedAt: optimisticMessage.createdAt,
      };
    });
    upsertConversationPreview(active.id, body || "📷 Photo", optimisticMessage.createdAt);
    if (!retryClientMessageId) {
      setMessage("");
    }

    try {
      const response = await sendMessage(active.id, body, clientMessageId, attachmentArg);
      if (response && response.message && response.conversationId) {
        applyConfirmedMessage(
          response.conversationId,
          response.message,
          response.updatedAt,
          response.lastMessage || response.message.body,
        );
        upsertConversationPreview(
          response.conversationId,
          response.lastMessage || response.message.body,
          response.updatedAt || response.message.createdAt,
        );
      } else if (response && response.id) {
        setActive(response);
        upsertConversationPreview(
          response.id,
          response.lastMessage || body,
          response.updatedAt || new Date().toISOString(),
        );
      }
    } catch (err) {
      const textError = String(err?.message || "Failed to send");
      markFailedByClientMessageId(active.id, clientMessageId, textError);
    }
  };

  const handleRetryMessage = async (msg) => {
    if (!msg?.body) return;
    await sendWithOptimistic(msg.body, msg.clientMessageId || buildClientMessageId());
  };

  const handlePickAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setAttaching(true);
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 1.5, maxWidthOrHeight: 1600, useWebWorker: true, fileType: "image/jpeg" });
      const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
      setAttachment(dataUrl);
    } catch (err) {
      showToast(String(err?.message || "Could not attach image."), "error");
    } finally {
      setAttaching(false);
    }
  };

  const handleSend = async () => {
    if (!active) return;
    const body = message.trim();
    const pendingAttachment = attachment;
    if (!body && !pendingAttachment) return;

    if (!body && pendingAttachment) {
      setAttachment(null);
      await sendWithOptimistic("", undefined, pendingAttachment);
      return;
    }

    // Check for banned terms and profanity
    const moderationCheck = containsBannedTerms(body);
    
    // Block hard-banned slurs
    if (moderationCheck.banned) {
      setBannedError(true);
      setTimeout(() => setBannedError(false), 5000);
      return; // Don't send message
    }
    
    // Warn about profanity but allow sending
    if (moderationCheck.profanity) {
      setProfanityWarning(true);
      setTimeout(() => setProfanityWarning(false), 5000);
    }
    
    // Check for scam words (Priority #6)
    const scamWords = ['paypal', 'venmo', 'cashapp', 'zelle', 'cash app'];
    const hasScamWord = scamWords.some(word => body.toLowerCase().includes(word));
    
    if (hasScamWord) {
      setScamWarning(true);
      setTimeout(() => setScamWarning(false), 5000);
    }

    setAttachment(null);
    await sendWithOptimistic(body, undefined, pendingAttachment);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!active || deletingId) return;
    const confirmed = await confirmToast({
      title: "Delete message?",
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "error",
    });
    if (!confirmed) return;

    setDeletingId(messageId);
    try {
      const updated = await deleteMessage(active.id, messageId);
      setActive(updated);
      setConversations((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (err) {
      showToast("Failed to delete message.", "error");
    } finally {
      setDeletingId("");
    }
  };

  const handleDeleteThread = async (conversationId, event) => {
    event.stopPropagation();
    if (deletingThreadId) return;
    const confirmed = await confirmToast({
      title: "Delete conversation?",
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "error",
    });
    if (!confirmed) return;

    setDeletingThreadId(conversationId);
    try {
      await deleteConversation(conversationId);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (active?.id === conversationId) {
        setActive(null);
        navigate("/messages");
      }
    } catch (err) {
      showToast("Failed to delete conversation.", "error");
    } finally {
      setDeletingThreadId("");
    }
  };

  const resolveViewListingTarget = (conversation) => {
    const listingId = String(conversation?.listingId || "").trim();
    if (listingId) return `/listing/${listingId}`;

    const sourceType = String(conversation?.sourceType || "").trim().toLowerCase();
    const offerId = String(conversation?.tradeOfferId || conversation?.offerId || "").trim();
    const sourceRefId = String(conversation?.sourceRefId || "").trim();
    const normalizedSourceRefId = sourceRefId.toUpperCase();

    if (sourceType === "trade_offer") {
      if (offerId) return `/offer/${offerId}`;
      if (sourceRefId) {
        if (normalizedSourceRefId.startsWith("TRADE-")) return `/trade-offer/${sourceRefId}`;
        if (normalizedSourceRefId.startsWith("BOUNTY-")) return `/bounty-offer/${sourceRefId}`;
      }
    }

    return "/local-trades";
  };

  return (
    <>
      <TopNav />
      <div className="messages page-with-top-nav">
        <header className="messages-header">
          <div className="messages-title">
            <button type="button" className="back-btn" onClick={() => navigate(-1)}>
              ←
            </button>
            <div>
              <h2>Messages</h2>
              <p>Chat with other traders about meetups and trades.</p>
            </div>
          </div>
        </header>

        {loading && (
          <div className="messages-empty">
            <h3>Loading conversations…</h3>
            <p>Please wait a moment.</p>
          </div>
        )}

      {error && (
        <div className="messages-empty">
          <h3>Couldn’t load conversations</h3>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="messages-shell">
          <aside className="messages-list">
            <div className="safety-banner">
              <span>🔒 Cardex is an 18+ platform. Do not share personal information. Meet in public places only.</span>
            </div>
            {conversations.length === 0 && (
              <div className="messages-empty">
                <h3>No conversations yet</h3>
                <p>Your inbox will show here once you start chatting.</p>
              </div>
            )}

            {conversations.map((conversation) => {
              const isActive = conversation.id === selectedId;
              const partner = conversation.sellerId === user?.id ? conversation.buyerName : conversation.sellerName;
              return (
                <div key={conversation.id} className="messages-item-row">
                  <button
                    className={`messages-item ${isActive ? "active" : ""}`}
                    type="button"
                    onClick={() => handleSelect(conversation.id)}
                  >
                    <div className="messages-item-top">
                      <span className="messages-partner">@{partner}</span>
                      <div className="messages-item-meta">
                        <span className="messages-time">
                          {new Date(conversation.updatedAt).toLocaleDateString()}
                        </span>
                        {Number(conversation.unreadCount || 0) > 0 && (
                          <span className="messages-thread-unread-badge">
                            {Number(conversation.unreadCount || 0) > 99
                              ? "99+"
                              : Number(conversation.unreadCount || 0)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="messages-item-body">
                      <span className="messages-listing">{conversation.listingTitle}</span>
                      <span className="messages-preview">{conversation.lastMessage}</span>
                    </div>
                  </button>
                  <button
                    className="delete-thread-btn"
                    type="button"
                    title="Delete conversation"
                    onClick={(event) => handleDeleteThread(conversation.id, event)}
                    disabled={deletingThreadId === conversation.id}
                  >
                    🗑️
                  </button>
                </div>
              );
            })}
          </aside>

          <section className="messages-thread">
            {!selectedId && (
              <div className="messages-empty">
                <h3>Select a conversation</h3>
                <p>Choose a thread to see the messages.</p>
              </div>
            )}

            {selectedId && threadLoading && (
              <div className="messages-empty">
                <h3>Loading thread…</h3>
                <p>Hang tight while we load the chat.</p>
              </div>
            )}

            {selectedId && !threadLoading && active && (
              <>
                {(() => {
                  const isOfferConversation =
                    String(active?.sourceType || "").toLowerCase() === "trade_offer" &&
                    Boolean(active?.offerId);

                  return (
                    <>
                <div className="thread-header">
                  <div>
                    <h3>{active.listingTitle}</h3>
                    <p>
                      {String(active?.sourceType || "").toLowerCase() === "trade_offer"
                        ? "Offer lifecycle chat"
                        : `${active.listingGame} • $${Number(active.listingPrice).toFixed(2)}`}
                    </p>
                  </div>
                  <div className="thread-header-actions">
                    {isOfferConversation && (
                      <button
                        className="btn-action"
                        type="button"
                        onClick={() => navigate(`/offer/${active.offerId}`)}
                      >
                        Complete Trade
                      </button>
                    )}
                    <button
                      className="btn-action secondary"
                      type="button"
                      onClick={() => navigate(resolveViewListingTarget(active))}
                    >
                      View Listing
                    </button>
                    <UserActionsMenu 
                      targetUserId={active.otherUserId} 
                      targetUserName={active.otherUserName} 
                    />
                  </div>
                </div>

                <div className="safety-banner chat">
                  <span>🔒 Cardex is an 18+ platform. Do not share personal information. Meet in public places only.</span>
                </div>

                <div className="thread-body">
                  {active.messages.map((msg, idx) => {
                    const isMine = msg.senderId === user?.id;
                    const isPending = msg.status === "pending";
                    const isFailed = msg.status === "failed";
                    const isFirstMessage = idx === 0;
                    const otherUserId = isMine ? active.otherUserId : msg.senderId;
                    const otherUserName = isMine ? active.otherUserName : (active.sellerName === user?.name ? active.buyerName : active.sellerName);
                    
                    return (
                      <div key={msg.id} className={`bubble ${isMine ? "mine" : "theirs"}`}>
                        {msg.attachmentUrl ? (
                          <a href={msg.attachmentUrl} target="_blank" rel="noreferrer">
                            <img className="bubble-attachment" src={msg.attachmentUrl} alt="attachment" />
                          </a>
                        ) : null}
                        {msg.body ? <p>{msg.body}</p> : null}
                        <div className="bubble-footer">
                          <span>
                            {isPending
                              ? "Sending…"
                              : isFailed
                              ? "Failed"
                              : new Date(msg.createdAt).toLocaleTimeString()}
                          </span>
                          {isMine && isFailed && (
                            <button
                              className="report-msg-btn"
                              onClick={() => handleRetryMessage(msg)}
                              title="Retry sending"
                              type="button"
                            >
                              Retry
                            </button>
                          )}
                          {isMine ? (
                            <button
                              className="delete-msg-btn"
                              onClick={() => handleDeleteMessage(msg.id)}
                              title="Delete this message"
                              type="button"
                              disabled={deletingId === msg.id || isPending}
                            >
                              🗑️
                            </button>
                          ) : (
                            <button 
                              className="report-msg-btn"
                              onClick={() => setReportModal({
                                open: true,
                                messageId: msg.id,
                                senderId: otherUserId,
                                senderName: otherUserName,
                                conversationId: active.id,
                                context: active.messages.slice(Math.max(0, idx - 3), idx + 1).map(m => m.body).join(' | ')
                              })}
                              title="Report this message"
                            >
                              ⚠️
                            </button>
                          )}
                          {isOfferConversation && isFirstMessage && active?.offerId && (
                            <button
                              type="button"
                              className="view-offer-btn"
                              onClick={() => navigate(`/offer/${active.offerId}`)}
                            >
                              View Offer
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="thread-input">
                  {bannedError && (
                    <div className="scam-warning error">
                      🚫 Message violates community guidelines. Please keep communication respectful.
                    </div>
                  )}
                  {profanityWarning && (
                    <div className="scam-warning warning">
                      ⚠️ Keep it respectful. Profanity may result in account restrictions.
                    </div>
                  )}
                  {scamWarning && (
                    <div className="scam-warning">
                      ⚠️ Warning: Off-app payments aren't protected. Keep all transactions on platform for safety.
                    </div>
                  )}
                  {attachment && (
                    <div className="composer-attachment">
                      <img src={attachment} alt="attachment preview" />
                      <button type="button" className="composer-attachment-remove" onClick={() => setAttachment(null)}>×</button>
                    </div>
                  )}
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handlePickAttachment}
                  />
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => attachmentInputRef.current?.click()}
                    title="Attach photo"
                    disabled={attaching}
                  >
                    {attaching ? "…" : "📎"}
                  </button>
                  <input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Type a message…"
                  />
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={handleSend}
                    disabled={!message.trim() && !attachment}
                  >
                    Send
                  </button>
                </div>
                    </>
                  );
                })()}
              </>
            )}
          </section>
        </div>
      )}
    </div>

    {reportModal.open && (
      <ReportUserModal
        targetUserId={reportModal.senderId}
        targetUserName={reportModal.senderName}
        onClose={() => setReportModal({ open: false, messageId: null, senderId: null, senderName: null })}
        prefillReason="Inappropriate message"
        prefillDetails={`Message ID: ${reportModal.messageId}\nConversation ID: ${reportModal.conversationId}\nContext: ${reportModal.context}`}
      />
    )}
    </>
  );
}
